/**
 * The company's own billing (`/billing/...`, docs/merge/10-billing.md): its
 * plan, this month's usage, its invoices. Amounts are exact strings; the
 * screens only show them.
 */
import { useQuery } from '@tanstack/vue-query'
import { api } from '@/lib/api'
import { can } from '@/lib/auth'

export type SubscriptionStatus = 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled'
export interface CompanyPlan {
  code: string; name: string; currency: string; monthlyFee: string; seatPrice: string; includedSeats: number
  minutePrice: string; includedMinutes: number; whatsappPrice: string; includedWhatsapp: number
  storageGbPrice: string; includedStorageGb: string; modules: string[]
}
export interface CompanyUsage {
  period: string; from: string; to: string; billedDays: number; monthDays: number
  seats: number; minutes: number; whatsapp: number; storageGb: string
  lines: Array<{ kind: string; label: string; quantity: string; unitPrice: string; amount: string }>
  estimate: string
}
export interface Overdue { id: string; number: string; dueOn: string; left: string; currency: string; suspendOn: string; pastGrace: boolean }
export interface CompanyBilling {
  subscription: { status: SubscriptionStatus; trialEndsOn: string | null; billingStartsOn: string; cancelledOn: string | null } | null
  plan: CompanyPlan | null
  usage: CompanyUsage | null
  overdue: Overdue | null
  payTo: { name: string; rib: string; email: string }
}
export interface CompanyInvoice {
  id: string; number: string; periodStart: string; periodEnd: string; status: 'issued' | 'paid' | 'void'
  currency: string; total: string; paidAmount: string; left: string
  issuedOn: string | null; dueOn: string | null; paidOn: string | null; overdue: boolean
}

export type InvoiceState = 'issued' | 'overdue' | 'paid' | 'void'
export const invoiceState = (i: Pick<CompanyInvoice, 'status' | 'overdue'>): InvoiceState =>
  (i.status === 'issued' && i.overdue ? 'overdue' : i.status)
export const INVOICE_STATES: InvoiceState[] = ['issued', 'overdue', 'paid', 'void']
export const invoiceTone = (s: InvoiceState) =>
  (s === 'paid' ? 'success' : s === 'overdue' ? 'danger' : s === 'issued' ? 'info' : 'neutral') as 'success' | 'danger' | 'info' | 'neutral'
export const statusTone = (s: SubscriptionStatus) =>
  (s === 'active' ? 'success' : s === 'trial' ? 'info' : s === 'past_due' ? 'warning' : s === 'suspended' ? 'danger' : 'neutral') as
    'success' | 'info' | 'warning' | 'danger' | 'neutral'

/** A month `YYYY-MM` as a date the formatters read. */
export const monthDay = (period: string): string => `${period.slice(0, 7)}-01`

/** A plan's price shown only when the plan charges it. */
export const charged = (price: string): boolean => /[1-9]/.test(price)

/** The company's billing, shared by its page and the late-payment banner (one cache key). */
export const useCompanyBilling = () => useQuery({
  queryKey: ['billing-subscription'],
  queryFn: () => api<CompanyBilling>('/billing/subscription'),
  enabled: can('billing.view'),
  staleTime: 5 * 60_000,
})
